import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { API_ENDPOINT } from "../../../config/api";
import { fetchWithAuth } from "../../../shared/api/fetch-with-auth";
import { AppThunk } from "../../../store";
import { ICropAggregationDto } from "../shared/dtos/crop-aggregation.dto";
import { OperationMode } from "../shared/enums/operation-mode";
import { IMapControlsState } from "../shared/interfaces/map-controls-state";
import { clampOperationTimestamp } from "../shared/utils/clamp-operation-timestamp";
import { getDefaultOperationDate } from "../shared/utils/get-default-operation-date";
import { getEndOfDayTimestamp } from "../shared/utils/get-end-of-day-timestamp";
import { getValidDayInMonth } from "../shared/utils/get-valid-day-in-month";
import {
  readStoredOperationMode,
  writeStoredOperationMode,
} from "../shared/utils/operation-mode-storage";

import { MAP_CONTROLS_MODULE_NAME } from "./constants";

const initialState: IMapControlsState = {
  isLoading: false,
  list: [],
  selected: [],
  opened: [],
  selectedFarmLands: [],
  searchFarmLandName: "",
  highlightedFarmLandId: null,
  activeOperationMode: readStoredOperationMode(),
  operationDate: getDefaultOperationDate(),
  seasonStartedAt: null,
};

export const mapControlsSlice = createSlice({
  name: MAP_CONTROLS_MODULE_NAME,
  initialState,
  reducers: {
    setHighlightedFarmLandId(state, action: PayloadAction<string | null>): void {
      state.highlightedFarmLandId = action.payload;
    },
    setIsLoadingAction(state, action: PayloadAction<boolean>): void {
      state.isLoading = action.payload;
    },
    setListAction(state, action: PayloadAction<ICropAggregationDto[]>): void {
      state.list = action.payload;
      state.selected = [];
      state.opened = [];
      state.selectedFarmLands = [];
    },
    toggleSelectedAction(state, action: PayloadAction<string>): void {
      const value = action.payload;
      const index = state.selected.indexOf(value);
      if (index !== -1) {
        state.selected.splice(index, 1);
      } else {
        state.selected.push(value);
      }
    },
    setOneSelectedAction(state, action: PayloadAction<string>): void {
      const value = action.payload;
      // https://tracker.yandex.ru/DEVELOPMENT-4138#67a9ce0800d9f850105f8e48
      // "при клике по культуре можно ли чтобы чекбокс проставлялся,
      //  при клике по другой культуре чекбокс из со старой снимался бы
      //  и проставлялся у новой (но это до разработки отдельной выборки, потом будет другое поведение)"
      const prevChecked = state.selected.pop(); // uncheck previously checked
      if (prevChecked === value) {
        state.selected = []; // none is checked
      } else {
        state.selected = [value]; // just one is checked
      }
    },
    toggleAllSelectedAction(state, _: PayloadAction): void {
      if (state.list.length === state.selected.length) {
        state.selected.length = 0;
      } else {
        state.selected = state.list.map((item) => item.cropTypeId);
      }
    },
    setOpenedAction(state, action: PayloadAction<string[]>): void {
      state.opened = action.payload;
    },
    toggleOpenedAction(state, action: PayloadAction<string>): void {
      const value = action.payload;
      const index = state.opened.indexOf(value);
      if (index !== -1) {
        state.opened = [];
      } else {
        state.opened = [value];
      }
      // если схлопнули культуру или открыли другую (не важно) - убираем фокус с поля
      state.highlightedFarmLandId = null;
    },
    toggleSelectedFarmLandsAction(state, action: PayloadAction<string>): void {
      const value = action.payload;
      const index = state.selectedFarmLands.indexOf(value);
      if (index !== -1) {
        state.selectedFarmLands.splice(index, 1);
      } else {
        state.selectedFarmLands.push(value);
      }
    },
    setSearchFarmLandNameAction(state, action: PayloadAction<string>): void {
      state.searchFarmLandName = action.payload;
    },
    setActiveOperationModeAction(
      state,
      action: PayloadAction<OperationMode | null>
    ): void {
      state.activeOperationMode = action.payload;
    },
    setOperationYearMonthAction(
      state,
      action: PayloadAction<{ year: number; month: number }>
    ): void {
      const { year, month } = action.payload;
      const currentDay = new Date(state.operationDate).getDate();
      const day = getValidDayInMonth(year, month, currentDay);
      const candidate = getEndOfDayTimestamp(year, month, day);
      state.operationDate = clampOperationTimestamp(candidate, state.seasonStartedAt);
    },
    setOperationDayAction(state, action: PayloadAction<number>): void {
      const current = new Date(state.operationDate);
      const year = current.getFullYear();
      const month = current.getMonth();
      const day = getValidDayInMonth(year, month, action.payload);
      const candidate = getEndOfDayTimestamp(year, month, day);
      state.operationDate = clampOperationTimestamp(candidate, state.seasonStartedAt);
    },
    setSeasonStartedAtAction(state, action: PayloadAction<number | null>): void {
      state.seasonStartedAt = action.payload;
      state.operationDate = clampOperationTimestamp(state.operationDate, action.payload);
    },
  },
  extraReducers(builder): void {
    builder
      .addCase(fetchCropAggregationAction.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchCropAggregationAction.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCropAggregationAction.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const mapControlsReducer = mapControlsSlice.reducer;
export const {
  setHighlightedFarmLandId,
  setListAction,
  setIsLoadingAction,
  toggleSelectedAction,
  setOneSelectedAction,
  toggleAllSelectedAction,
  toggleOpenedAction,
  toggleSelectedFarmLandsAction,
  setOpenedAction,
  setSearchFarmLandNameAction,
  setActiveOperationModeAction,
  setOperationYearMonthAction,
  setOperationDayAction,
  setSeasonStartedAtAction,
} = mapControlsSlice.actions;

export function setActiveOperationMode(mode: OperationMode | null): AppThunk {
  return (dispatch) => {
    dispatch(setActiveOperationModeAction(mode));
    writeStoredOperationMode(mode);
  };
}

export const fetchCropAggregationAction = createAsyncThunk<
  void,
  { farmId: string; seasonName: string; filter?: unknown }
>(
  `${MAP_CONTROLS_MODULE_NAME}/fetchCropAggregationAction`,
  async ({ farmId, seasonName }, { dispatch }) => {
    const cropParams = new URLSearchParams({ seasonName });
    const cropResponse = await fetchWithAuth(
      `${API_ENDPOINT}/FarmsV2/${farmId}/cropAggregation?${cropParams}`
    );
    if (!cropResponse.ok) {
      throw await cropResponse.json();
    }
    const dtos: ICropAggregationDto[] = await cropResponse.json();
    dispatch(setListAction(dtos));
  }
);
